/**
 * Client-side cookie consent state.
 *
 * Cookies (all `SameSite=Lax`, `Secure` in production, 1y maxAge):
 *   - relm_anon_id              UUID v4, stable per browser. Issued on first
 *                               page load. Used to correlate consent events
 *                               and stitch pre-auth → post-auth sessions.
 *   - relm_consent_ack          ISO-8601 timestamp of the most recent
 *                               acknowledgement. Presence of this cookie
 *                               is the single source of truth for "should
 *                               we show the banner". Absence → show banner.
 *   - relm_consent_analytics    'true' | 'false'
 *   - relm_consent_ai_training  'true' | 'false'
 *   - relm_consent_marketing    'true' | 'false'
 *
 * We deliberately use four small string cookies instead of one JSON blob
 * so server components / middleware can read individual prefs cheaply
 * via cookies().get(), without parsing.
 */

import type { ConsentAction } from '@/types/consent';

export const COOKIE_NAMES = {
  ANON_ID: 'relm_anon_id',
  ACK: 'relm_consent_ack',
  ANALYTICS: 'relm_consent_analytics',
  AI_TRAINING: 'relm_consent_ai_training',
  MARKETING: 'relm_consent_marketing',
} as const;

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export interface ConsentPrefs {
  analytics: boolean;
  ai_training: boolean;
  marketing: boolean;
}

export interface ConsentState extends ConsentPrefs {
  acknowledged: boolean;
  acknowledgedAt: string | null;
  anonymousId: string;
}

function isBrowser(): boolean {
  return typeof document !== 'undefined';
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const match = document.cookie.match(
    new RegExp(
      `(?:^|; )${name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&')}=([^;]*)`,
    ),
  );
  const raw = match?.[1];
  if (raw === undefined || raw === '') return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function writeCookie(name: string, value: string, maxAge = ONE_YEAR_SECONDS) {
  if (!isBrowser()) return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge};` +
    ` SameSite=Lax${secure}`;
}

function deleteCookie(name: string) {
  if (!isBrowser()) return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Generate a UUID v4. Uses the native `crypto.randomUUID` when available
 * (all modern evergreen browsers) and falls back to a manual implementation
 * so we don't crash on ancient Safari or in non-secure contexts.
 */
function uuidv4(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Crockford-style fallback. These identifiers need collision resistance,
  // not secrecy; this keeps older/non-secure browser contexts functional.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

/**
 * Returns the existing anonymous id, issuing and persisting a new one on
 * first call. Idempotent within a single session.
 */
export function getOrCreateAnonymousId(): string {
  const existing = readCookie(COOKIE_NAMES.ANON_ID);
  if (existing) return existing;
  const fresh = uuidv4();
  writeCookie(COOKIE_NAMES.ANON_ID, fresh);
  return fresh;
}

export function readConsentState(): ConsentState {
  const ack = readCookie(COOKIE_NAMES.ACK);
  const analytics = readCookie(COOKIE_NAMES.ANALYTICS);
  const aiTraining = readCookie(COOKIE_NAMES.AI_TRAINING);
  const marketing = readCookie(COOKIE_NAMES.MARKETING);
  return {
    anonymousId: getOrCreateAnonymousId(),
    acknowledged: Boolean(ack),
    acknowledgedAt: ack,
    analytics: analytics === 'true',
    ai_training: aiTraining === 'true',
    marketing: marketing === 'true',
  };
}

/** The former single-slot queue, retained as read-only migration input. */
const LEGACY_PENDING_KEY = 'relm_pending_consent_v1';
/** Exact v1 bytes last handled; separate so migration never mutates v1. */
const LEGACY_MIGRATION_MARKER_KEY = 'relm_pending_consent_v1_migrated_raw';
/** Every v2 event has its own key, so unrelated choices never share a lock. */
const OUTBOX_KEY_PREFIX = 'relm_pending_consent_v2:';
const MIN_ANONYMOUS_ID_LENGTH = 8;
const MAX_ANONYMOUS_ID_LENGTH = 128;
const EVENT_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
/**
 * Old outbox formats cannot recover when the user made the choice. Sorting
 * them at the epoch keeps an unknown delayed retry from superseding a newer,
 * timestamped choice, and guarantees the API will not attach current auth.
 */
const UNKNOWN_OCCURRED_AT = '1970-01-01T00:00:00.000Z';

interface ConsentPayload {
  anonymous_id: string;
  analytics: boolean;
  ai_training: boolean;
  marketing: boolean;
  action: ConsentAction;
}

interface PendingConsent extends ConsentPayload {
  event_id: string;
  occurred_at: string;
}

interface LegacyPendingConsent extends ConsentPayload {
  outbox_id?: string;
}

function isConsentAction(value: unknown): value is ConsentAction {
  return (
    value === 'accept_all' ||
    value === 'reject_non_essential' ||
    value === 'customize' ||
    value === 'revoke'
  );
}

function isCanonicalStoredTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value;
}

function parseStoredObject(raw: string): Record<string, unknown> | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function parseConsentPayload(
  candidate: Record<string, unknown>,
): ConsentPayload | null {
  if (
    typeof candidate.anonymous_id !== 'string' ||
    candidate.anonymous_id.length < MIN_ANONYMOUS_ID_LENGTH ||
    candidate.anonymous_id.length > MAX_ANONYMOUS_ID_LENGTH ||
    typeof candidate.analytics !== 'boolean' ||
    typeof candidate.ai_training !== 'boolean' ||
    typeof candidate.marketing !== 'boolean' ||
    !isConsentAction(candidate.action)
  ) {
    return null;
  }

  return {
    anonymous_id: candidate.anonymous_id,
    analytics: candidate.analytics,
    ai_training: candidate.ai_training,
    marketing: candidate.marketing,
    action: candidate.action,
  };
}

function parsePendingConsent(
  raw: string,
  expectedEventId: string,
): PendingConsent | null {
  const candidate = parseStoredObject(raw);
  if (!candidate) return null;
  const payload = parseConsentPayload(candidate);
  const occurredAt =
    candidate.occurred_at === undefined ?
      UNKNOWN_OCCURRED_AT
    : candidate.occurred_at;
  if (
    !payload ||
    typeof candidate.event_id !== 'string' ||
    candidate.event_id !== expectedEventId ||
    !EVENT_ID_PATTERN.test(candidate.event_id) ||
    !isCanonicalStoredTimestamp(occurredAt)
  ) {
    return null;
  }
  return {
    event_id: candidate.event_id,
    occurred_at: occurredAt,
    ...payload,
  };
}

function parseLegacyConsent(raw: string): LegacyPendingConsent | null {
  const candidate = parseStoredObject(raw);
  if (!candidate) return null;
  const payload = parseConsentPayload(candidate);
  if (
    !payload ||
    (candidate.outbox_id !== undefined &&
      (typeof candidate.outbox_id !== 'string' ||
        !EVENT_ID_PATTERN.test(candidate.outbox_id)))
  ) {
    return null;
  }
  return {
    ...payload,
    ...(typeof candidate.outbox_id === 'string' ?
      { outbox_id: candidate.outbox_id }
    : {}),
  };
}

function consentPayload(entry: ConsentPayload): ConsentPayload {
  return {
    anonymous_id: entry.anonymous_id,
    analytics: entry.analytics,
    ai_training: entry.ai_training,
    marketing: entry.marketing,
    action: entry.action,
  };
}

function outboxKey(eventId: string): string {
  return `${OUTBOX_KEY_PREFIX}${eventId}`;
}

/**
 * v1 has no identity, so use a stable 128-bit content fingerprint. This lets
 * racing tabs converge on one server event. Byte-identical legacy writes are
 * necessarily treated as retries because the old format cannot distinguish
 * them.
 */
function legacyEventId(raw: string): string {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let index = 0; index < raw.length; index += 1) {
    const code = raw.charCodeAt(index);
    h1 = h2 ^ Math.imul(h1 ^ code, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ code, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ code, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ code, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  const fingerprint = [h1, h2, h3, h4]
    .map((value) => (value >>> 0).toString(16).padStart(8, '0'))
    .join('');
  return `legacy_${fingerprint}`;
}

/**
 * POST one consent event. Resolves true only when the server confirms it
 * stored the row.
 *
 * `fetch` resolves for a 5xx and rejects only on a transport error, so the
 * status has to be checked explicitly — without that, a failed audit write
 * looked exactly like a successful one (RELM-349).
 */
async function postConsent(payload: PendingConsent): Promise<boolean> {
  try {
    const res = await fetch('/api/consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `keepalive: true` lets the request finish even if the user
      // navigates away immediately after clicking the banner button.
      keepalive: true,
      body: JSON.stringify(payload),
    });

    return res.ok;
  } catch {
    return false;
  }
}

/** Persist one independently keyed event before attempting delivery. */
function stashPending(
  entry: PendingConsent,
): { key: string; raw: string } | null {
  if (typeof window === 'undefined') return null;

  const key = outboxKey(entry.event_id);
  const raw = JSON.stringify(entry);
  try {
    const existing = window.localStorage.getItem(key);
    if (existing !== null && existing !== raw) {
      console.error('[consent] local outbox event id collision');
      return null;
    }
    window.localStorage.setItem(key, raw);
    return { key, raw };
  } catch (error) {
    // Private-mode Safari and a full quota both throw here. Legacy callers
    // retain their read-only source so a later load can try again.
    console.error('[consent] could not stash the pending event:', error);
    return null;
  }
}

/** Remove only the exact value this operation read or wrote. */
function removePendingIfCurrent(key: string, expectedRaw: string): void {
  try {
    if (window.localStorage.getItem(key) === expectedRaw) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Keeping an already-delivered entry is safe because the server dedupes
    // identified replays.
  }
}

/** Upgrade an entry in place only while its original bytes are still current. */
function replacePendingIfCurrent(
  key: string,
  expectedRaw: string,
  entry: PendingConsent,
): string | null {
  const replacementRaw = JSON.stringify(entry);
  if (replacementRaw === expectedRaw) return expectedRaw;
  try {
    if (window.localStorage.getItem(key) !== expectedRaw) return null;
    window.localStorage.setItem(key, replacementRaw);
    return replacementRaw;
  } catch {
    return null;
  }
}

function readOutboxKeys(): string[] {
  const keys: string[] = [];
  try {
    const length = window.localStorage.length;
    for (let index = 0; index < length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(OUTBOX_KEY_PREFIX)) keys.push(key);
    }
  } catch {
    return [];
  }
  return keys;
}

function readLegacyMigrationMarker(): string | null {
  try {
    return window.localStorage.getItem(LEGACY_MIGRATION_MARKER_KEY);
  } catch {
    // Missing the optimization only causes a deterministic server replay.
    return null;
  }
}

function markLegacyRawHandled(raw: string): void {
  try {
    window.localStorage.setItem(LEGACY_MIGRATION_MARKER_KEY, raw);
  } catch {
    // The v1 bytes remain untouched. A later load may restash the same
    // deterministic event, which the server treats as an idempotent replay.
  }
}

/**
 * Copy the read-only v1 slot into an independently keyed v2 entry.
 *
 * Never delete or rewrite v1: localStorage cannot atomically compare/remove,
 * so an old tab could otherwise write a distinct choice between those steps
 * and have it erased. The exact-raw marker is written only after the v2 copy
 * is durable. Marker races can therefore cause only deterministic replays.
 */
function migrateLegacyPending(): void {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(LEGACY_PENDING_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  if (readLegacyMigrationMarker() === raw) return;

  const legacy = parseLegacyConsent(raw);
  if (!legacy) {
    console.error('[consent] legacy pending event was unreadable, ignoring');
    markLegacyRawHandled(raw);
    return;
  }

  const entry: PendingConsent = {
    event_id: legacy.outbox_id ?? legacyEventId(raw),
    occurred_at: UNKNOWN_OCCURRED_AT,
    ...consentPayload(legacy),
  };
  const stashed = stashPending(entry);
  if (stashed === null) return;
  markLegacyRawHandled(raw);
}

async function flushOutboxKey(key: string): Promise<void> {
  const eventId = key.slice(OUTBOX_KEY_PREFIX.length);
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return;
  }
  if (!raw) return;

  const entry = parsePendingConsent(raw, eventId);
  if (!entry) {
    console.error('[consent] pending event was unreadable, dropping');
    removePendingIfCurrent(key, raw);
    return;
  }

  // Entries written by the previous v2 client had no occurrence timestamp.
  // Persist the conservative sentinel before POST so every retry is identical.
  const currentRaw = replacePendingIfCurrent(key, raw, entry);
  if (currentRaw === null) return;
  if (await postConsent(entry)) removePendingIfCurrent(key, currentRaw);
}

/**
 * Deliver a consent event held back by an earlier failure.
 *
 * The cookies are written before the POST and the banner never re-prompts, so
 * without this a failed write is permanent loss rather than a delay. Safe to
 * call on every load: it is a no-op with nothing pending.
 */
export async function flushPendingConsent(): Promise<void> {
  if (typeof window === 'undefined') return;

  migrateLegacyPending();
  await Promise.all(readOutboxKeys().map(flushOutboxKey));
}

/**
 * Persist a new set of preferences locally and POST them to the audit
 * endpoint. Local cookies are written first so the banner doesn't re-prompt
 * on the next page load if the API is briefly unreachable — and because that
 * means the user is never asked again, a failed POST is queued rather than
 * dropped.
 */
export async function persistConsent(
  prefs: ConsentPrefs,
  action: ConsentAction,
): Promise<void> {
  const anonymousId = getOrCreateAnonymousId();
  const acknowledgedAt = new Date().toISOString();

  writeCookie(COOKIE_NAMES.ANALYTICS, String(prefs.analytics));
  writeCookie(COOKIE_NAMES.AI_TRAINING, String(prefs.ai_training));
  writeCookie(COOKIE_NAMES.MARKETING, String(prefs.marketing));
  writeCookie(COOKIE_NAMES.ACK, acknowledgedAt);

  const payload: ConsentPayload = {
    anonymous_id: anonymousId,
    analytics: prefs.analytics,
    ai_training: prefs.ai_training,
    marketing: prefs.marketing,
    action,
  };
  const entry: PendingConsent = {
    event_id: uuidv4(),
    occurred_at: acknowledgedAt,
    ...payload,
  };

  // Write the outbox first. Its unique identity distinguishes two identical
  // choices, so an older request can never clear a newer pending write.
  const pending = stashPending(entry);

  // Start the keepalive request before the consent listener can synchronously
  // reload the page, but do not delay delivery of the consent-change event.
  const postPromise = postConsent(entry);

  // Notify any subsystem that gates itself on consent (currently:
  // PostHog product analytics in lib/posthog.ts) so it can react
  // without waiting for the next page load.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<ConsentPrefs>('cookie-consent-changed', {
        detail: { ...prefs },
      }),
    );
  }

  if ((await postPromise) && pending !== null) {
    removePendingIfCurrent(pending.key, pending.raw);
  }
}

/**
 * Clear all consent cookies (without removing the anonymous_id, which we
 * keep so the next consent is correlated with the prior session). Forces
 * the banner back into view on the next page load.
 */
export async function revokeConsent(): Promise<void> {
  await persistConsent(
    { analytics: false, ai_training: false, marketing: false },
    'revoke',
  );
  deleteCookie(COOKIE_NAMES.ACK);
  deleteCookie(COOKIE_NAMES.ANALYTICS);
  deleteCookie(COOKIE_NAMES.AI_TRAINING);
  deleteCookie(COOKIE_NAMES.MARKETING);
}

/** Convenience: matches what the banner emits for the two-button flow. */
export const ACCEPT_ALL_PREFS: ConsentPrefs = {
  analytics: true,
  ai_training: true,
  marketing: true,
};

export const REJECT_NON_ESSENTIAL_PREFS: ConsentPrefs = {
  analytics: false,
  ai_training: false,
  marketing: false,
};
